// Fase 15A — Scheda utente (punto 42): account, organizzazione, ruoli,
// assegnazioni, stato, MFA, inviti. Mai token o segreti (punto 43).
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Users2, ShieldCheck, KeyRound, Copy, Landmark } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function PlatformUserDetail() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [resetLink, setResetLink] = useState(null);

  const resetAccess = async () => {
    try {
      const { data: r } = await api.post(
        `/admin/platform/users/${userId}/reset-access`);
      setResetLink(`${window.location.origin}${r.reset_link}`);
      toast.success("Link di reset generato", {
        description: "Monouso, valido 60 minuti. Consegnalo all'utente." });
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const toggleOrgAdmin = async () => {
    try {
      await api.put(`/admin/platform/users/${userId}/org-admin`,
        { org_admin: !data.user.org_admin });
      toast.success(data.user.org_admin
        ? "Ruolo Admin di Studio revocato" : "Utente promosso ad Admin di Studio");
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/admin/platform/users/${userId}`);
      setData(d);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-red-700" data-testid="user-detail-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="user-detail-loading">Caricamento…</p>;
  const { user, organization, memberships, assignments, invitations, mfa_enrolled } = data;

  return (
    <div className="space-y-6" data-testid="platform-user-detail">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Users2 className="w-7 h-7 text-green-800" aria-hidden="true" />
          {user.full_name}
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          {user.email} · stato <strong>{user.status || "ACTIVE"}</strong>
          {user.last_login_at && ` · ultimo accesso ${user.last_login_at.slice(0, 16).replace("T", " ")}`}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200" data-testid="user-account-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Account</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-slate-500">Organizzazione</span>
              <strong data-testid="user-org">{organization?.name || "—"}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Lingua</span>
              <strong>{user.language || "it"}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">MFA</span>
              <Badge variant="outline" className={mfa_enrolled
                ? "bg-green-50 text-green-800 border-green-300"
                : "bg-slate-50 text-slate-600 border-slate-300"}
                data-testid="user-mfa-status">
                {mfa_enrolled ? "ATTIVO" : "non configurato"}</Badge></div>
            <div className="flex justify-between"><span className="text-slate-500">Super Admin</span>
              <strong>{user.is_superadmin ? "sì" : "no"}</strong></div>
            <div className="flex justify-between"><span className="text-slate-500">Admin di Studio</span>
              <strong data-testid="user-org-admin-flag">{user.org_admin ? "sì" : "no"}</strong></div>
            {!user.is_superadmin && (
              <div className="pt-2 space-y-2">
                <Button variant="outline" size="sm" onClick={resetAccess}
                  data-testid="user-reset-access-button">
                  <KeyRound className="w-3 h-3 mr-1" aria-hidden="true" /> Reset accesso
                </Button>
                {resetLink && (
                  <div className="flex items-center gap-2 rounded-md bg-green-50 border border-green-200 p-2"
                    data-testid="user-reset-link">
                    <code className="text-xs text-green-900 truncate flex-1">{resetLink}</code>
                    <Button size="sm" variant="outline" className="h-7"
                      data-testid="user-reset-copy"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(resetLink);
                          toast.success("Link copiato");
                        } catch {
                          toast.info("Copia manualmente il link selezionandolo");
                        }
                      }}>
                      <Copy className="w-3 h-3 mr-1" aria-hidden="true" /> Copia
                    </Button>
                  </div>
                )}
                {organization && (
                  <Button variant="outline" size="sm" onClick={toggleOrgAdmin}
                    data-testid="user-org-admin-toggle">
                    <Landmark className="w-3 h-3 mr-1" aria-hidden="true" />
                    {user.org_admin ? "Revoca Admin di Studio" : "Promuovi ad Admin di Studio"}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="user-invitations-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Inviti recenti</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {invitations.length === 0 && (
              <p className="text-xs text-slate-500">Nessun invito.</p>)}
            {invitations.map((i) => (
              <div key={i.id} className="flex items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                data-testid={`user-invitation-${i.id}`}>
                <Badge variant="outline" className="text-[9px]">{i.role}</Badge>
                <span className="text-slate-600 flex-1">{i.created_at?.slice(0, 10)}</span>
                <Badge variant="outline" className={i.status === "ACCEPTED"
                  ? "bg-green-50 text-green-800 border-green-300 text-[9px]"
                  : i.status === "PENDING" ? "bg-sky-50 text-sky-800 border-sky-300 text-[9px]"
                  : "bg-slate-100 text-slate-500 border-slate-300 text-[9px]"}>{i.status}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="user-memberships-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" aria-hidden="true" /> Ruoli e aziende
        </CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {memberships.length === 0 && (
            <p className="text-xs text-slate-500">Nessuna membership aziendale.</p>)}
          {memberships.map((m) => (
            <div key={m.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
              data-testid={`user-membership-${m.id}`}>
              <Badge variant="outline" className="text-[9px]">{m.role}</Badge>
              <span className="font-medium text-slate-800">{m.company?.denominazione}</span>
              <span className="text-slate-400">{m.permissions.length} permessi</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {assignments.length > 0 && (
        <Card className="border-slate-200" data-testid="user-assignments-card">
          <CardHeader className="pb-2"><CardTitle className="text-base">Assegnazioni consulente</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {assignments.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center gap-2 text-xs border-b border-slate-100 pb-1.5"
                data-testid={`user-assignment-${a.id}`}>
                <Badge variant="outline" className="text-[9px]">{a.consultant_role}</Badge>
                <span className="font-medium text-slate-800">{a.company_name}</span>
                <Badge variant="outline" className={a.status === "ACTIVE"
                  ? "bg-green-50 text-green-800 border-green-300 text-[9px]"
                  : "bg-slate-100 text-slate-500 border-slate-300 text-[9px]"}>{a.status}</Badge>
                <span className="text-slate-400">dal {a.valid_from?.slice(0, 10)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
